import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { removeBookDownload, requestBookDownload, type DownloadRequest } from '@/native/bookDownloads';
import { bufferScheduler, type BufferState, type PinnedBookProgress } from '@/native/bufferScheduler';
import {
  estimateCatalogBookBytes,
  estimateReaderBookBytes,
  formatDownloadSize,
  type SizeEstimate,
} from '@/native/downloadPreflight';
import type { ReaderBook } from '@/services/booksApi';
import type { PinnedBook } from '@/services/offlineSettings';
import type { Book } from '@/utils/bookRouting';
import { useOfflineSettings } from './useOfflineSettings';

/**
 * What a "Download book" control shows for one book, derived from the two
 * things that are actually stored — the pin in the offline settings and the
 * files in the asset store, as the buffer scheduler reports them. Nothing
 * here is a record of its own, so it cannot drift from either.
 */

/** Why a pinned book is not fetching right now. */
export type DownloadWait =
  /** The scheduler has not got the book's payload yet. */
  | 'preparing'
  | 'offline'
  /** On cellular with the mobile-data switch off. */
  | 'cellular-off'
  /** The store is at its cap and nothing finished can be evicted. */
  | 'storage-full'
  /** Some files were given up on this session; they are tried again later. */
  | 'blocked';

export type BookDownloadState =
  | { kind: 'none'; size: SizeEstimate }
  | { kind: 'downloading'; chaptersDone: number; chaptersTotal: number; waiting: DownloadWait | null }
  | { kind: 'downloaded'; bytes: number };

interface DeriveInput {
  pin: PinnedBook | null;
  progress: PinnedBookProgress | null;
  bufferState: BufferState;
  size: SizeEstimate;
}

export const deriveBookDownloadState = ({ pin, progress, bufferState, size }: DeriveInput): BookDownloadState => {
  if (!pin) {
    return { kind: 'none', size };
  }
  if (progress?.complete) {
    return { kind: 'downloaded', bytes: progress.bytes };
  }
  let waiting: DownloadWait | null = null;
  if (bufferState === 'offline') {
    waiting = 'offline';
  } else if (bufferState === 'cellular-off') {
    waiting = 'cellular-off';
  } else if (bufferState === 'cap-reached') {
    waiting = 'storage-full';
  } else if (!progress) {
    waiting = 'preparing';
  } else if (bufferState === 'window-full' && progress.blocked > 0) {
    // Nothing left that the scheduler is willing to try this session.
    waiting = 'blocked';
  }
  return {
    kind: 'downloading',
    chaptersDone: progress?.chaptersDone ?? 0,
    chaptersTotal: progress?.chaptersTotal ?? 0,
    waiting,
  };
};

export const bookDownloadLabel = (state: BookDownloadState): string => {
  if (state.kind === 'none') return 'Download book';
  return state.kind === 'downloaded' ? 'Downloaded' : 'Downloading';
};

/** The short figure beside the label: a size, a count of chapters, or what it is waiting for. */
export const bookDownloadDetail = (state: BookDownloadState): string => {
  if (state.kind === 'none') {
    return formatDownloadSize(state.size);
  }
  if (state.kind === 'downloaded') {
    return formatDownloadSize({ bytes: state.bytes, estimated: false });
  }
  switch (state.waiting) {
    case 'preparing':
      return 'Starting…';
    case 'offline':
      return 'Waiting for connection';
    case 'cellular-off':
      return 'Waiting for Wi-Fi';
    case 'storage-full':
      return 'Storage full';
    case 'blocked':
      return 'Paused, will retry';
    default:
      return `${state.chaptersDone} of ${state.chaptersTotal} chapters`;
  }
};

interface UseBookDownloadArgs {
  book: Book;
  /** The open book's payload, when it is this book; a download is asked for from the player. */
  readerBook: ReaderBook | null;
  voiceKey: string;
}

export interface BookDownload {
  state: BookDownloadState;
  /** False until the payload is at hand: the preflight and the pin both need it. */
  canRequest: boolean;
  /** Run the size preflight and pin the book if it fits. Null when it cannot be asked yet. */
  request: () => DownloadRequest | null;
  remove: () => Promise<void>;
}

export const useBookDownload = ({ book, readerBook, voiceKey }: UseBookDownloadArgs): BookDownload => {
  const { pinnedBooks } = useOfflineSettings();
  const status = useSyncExternalStore(bufferScheduler.subscribe, bufferScheduler.getStatus, bufferScheduler.getStatus);
  const payload = readerBook?.id === book.id ? readerBook : null;

  const size = useMemo(
    () => (payload && voiceKey ? estimateReaderBookBytes(payload, voiceKey) : estimateCatalogBookBytes(book)),
    [payload, voiceKey, book],
  );

  const state = deriveBookDownloadState({
    pin: pinnedBooks.find((pin) => pin.bookId === book.id) ?? null,
    progress: status.pinned.find((entry) => entry.bookId === book.id) ?? null,
    bufferState: status.state,
    size,
  });

  const request = useCallback(
    // The book being asked for is the open one, so its own space is never counted as reclaimable.
    () => (payload && voiceKey ? requestBookDownload(payload, voiceKey, payload.id) : null),
    [payload, voiceKey],
  );
  const remove = useCallback(() => removeBookDownload(book.id), [book.id]);

  return { state, canRequest: payload !== null && voiceKey !== '', request, remove };
};
